/**
 * One reading of a phone number for both what people see and what they dial.
 * Display is the Australian local way, as on a van door: no brackets, no country
 * code. The `tel:` link is E.164, except for 13, 1300 and 1800 numbers, which only
 * work inside Australia and stay in national form. Anything that isn't recognisably
 * Australian shows as typed, and links only when it is a plausible international
 * number.
 */

type Reading = { shown: string; href: string | null }

const group = (digits: string, ...sizes: number[]) => {
  const parts: string[] = []
  let at = 0
  for (const size of sizes) {
    parts.push(digits.slice(at, at + size))
    at += size
  }
  return parts.join(' ')
}

function read(raw: string): Reading {
  const typed = raw.trim()
  // A plus belongs at the front only, and letters or stray symbols mean it is a note.
  if (!/^\+?[\d\s().-]+$/.test(typed) || !/\d/.test(typed)) return { shown: typed, href: null }
  const international = typed.startsWith('+')
  const digits = typed.replace(/\D/g, '')

  // "+61 (0)3 ..." carries the trunk zero people write beside the country code.
  let national = digits
  if (international) {
    national = digits.startsWith('61') ? `0${digits.slice(2).replace(/^0/, '')}` : ''
  } else if (digits.startsWith('61') && digits.length === 11) {
    national = `0${digits.slice(2)}`
  }

  if (/^04\d{8}$/.test(national)) {
    return { shown: group(national, 4, 3, 3), href: `tel:+61${national.slice(1)}` }
  }
  if (/^0[2378]\d{8}$/.test(national)) {
    return { shown: group(national, 2, 4, 4), href: `tel:+61${national.slice(1)}` }
  }
  if (!international && /^1[38]00\d{6}$/.test(national)) {
    return { shown: group(national, 4, 3, 3), href: `tel:${national}` }
  }
  if (!international && /^13\d{4}$/.test(national)) {
    return { shown: group(national, 2, 2, 2), href: `tel:${national}` }
  }
  // E.164: up to 15 digits, never starting with 0.
  const plausible = international && /^[1-9]\d{7,14}$/.test(digits)
  return { shown: typed, href: plausible ? `tel:+${digits}` : null }
}

export function displayPhone(raw: string): string {
  return read(raw).shown
}

/** A `tel:` link, or null when the text isn't a number anyone could dial. */
export function phoneHref(raw: string): string | null {
  return read(raw).href
}
